import type {
  EditableComponentsMap,
  PromptOptionsForEditableComponents,
  PromptOptionValue,
  PromptSuggestionsEnum,
  PromptSuggestionsValue,
  RedesignIssueType,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {
  DefaultPromptSuggestions,
  EditableComponent,
  EmailSingleRewrite,
  EmojiUsage,
  ImageModel,
  Mood,
  PromptOption,
  PromptOptionSelectorLayoutType,
  PromptSuggestions,
  RestaurantPromptSuggestions,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import type {Filter} from '@Components/checkbox-filters/checkbox-filters.types';
import {CHECKBOX_TYPE} from '@Components/checkbox';
import {IconSize} from '@Components/icon-v2/icon.types';
import {TextSize} from '@Components/text';

export const RECENT_PROMPT_DISPLAY_LENGTH = 30;
export const RECENT_PROMPT_SUGGESTION_PILL_KEY = 'recent-prompt';
export const RECENT_PROMPT_SUGGESTION_PILL_ICON = 'icon-history';

export const getDefaultPromptOptionValue = (option: PromptOption): PromptOptionValue => {
  switch (option) {
    case PromptOption.MOOD:
      return Mood.Formal;
    case PromptOption.EMOJI_USAGE:
      return EmojiUsage.Disabled;
    case PromptOption.IMAGE_MODEL:
      return ImageModel.Flux;
  }
};

export const getPromptOptionValueContent = (value: PromptOptionValue | null): string => {
  if (!value) {
    return '';
  }

  switch (value) {
    case EmojiUsage.Disabled:
      return `👎 ${window.i18next.t('pmwjs_no')}`;
    case EmojiUsage.Enabled:
      return `👍 ${window.i18next.t('pmwjs_yes')}`;
    case Mood.Formal:
      return `👔 ${window.i18next.t('pmwjs_formal')}`;
    case Mood.Bold:
      return `🔥 ${window.i18next.t('pmwjs_bold')}`;
    case Mood.Minimal:
      return `🌱 ${window.i18next.t('pmwjs_minimal')}`;
    case Mood.Elegant:
      return `👒 ${window.i18next.t('pmwjs_elegant')}`;
    case Mood.Vibrant:
      return `🌈 ${window.i18next.t('pmwjs_vibrant')}`;
    case Mood.Modern:
      return `⚡ ${window.i18next.t('pmwjs_modern_style')}`;
    case Mood.Classic:
      return `📜 ${window.i18next.t('pmwjs_classic')}`;
    case Mood.Creative:
      return `🎨 ${window.i18next.t('pmwjs_creative')}`;
    case Mood.Playful:
      return `🪩 ${window.i18next.t('pmwjs_playful')}`;
    case Mood.Sleek:
      return `💎 ${window.i18next.t('pmwjs_sleek')}`;
    case ImageModel.Flux:
      return window.i18next.t('pmwjs_flux');
    case ImageModel.Stability:
      return window.i18next.t('pmwjs_stability');
  }
};

export const getPromptOptionValueDescription = (value: PromptOptionValue): string => {
  switch (value) {
    case ImageModel.Flux:
      return window.i18next.t('pmwjs_flux_desc');
    case ImageModel.Stability:
      return window.i18next.t('pmwjs_stability_core_desc');
    default:
      return '';
  }
};

export const getPromptSuggestionsPillTitle = (value: PromptSuggestionsValue | null): string => {
  if (!value) {
    return '';
  }

  switch (value) {
    case DefaultPromptSuggestions.CHURCH_NEWLETTER:
      return `📖 ${window.i18next.t('pmwjs_church_newsletter')}`;
    case DefaultPromptSuggestions.RESTAURANT_DAILY_SPECIALS:
      return `🍽️ ${window.i18next.t('pmwjs_daily_specials')}`;
    case DefaultPromptSuggestions.SALE:
      return `🛍️ ${window.i18next.t('pmwjs_store_sale')}`;
    case DefaultPromptSuggestions.SCHOOL_NEWSLETTER:
      return `🏫 ${window.i18next.t('pmwjs_school_newsletter')}`;
    case RestaurantPromptSuggestions.BAR_MENU:
      return `🍹 ${window.i18next.t('pmwjs_bar_menu')}`;
    case RestaurantPromptSuggestions.HAPPY_HOUR:
      return `🎉 ${window.i18next.t('pmwjs_happy_hour')}`;
    case RestaurantPromptSuggestions.COCKTAIL_HOUR:
      return `🍸 ${window.i18next.t('pmwjs_cocktail_hour')}`;
    case RestaurantPromptSuggestions.CAFE_DAILY_SPECIALS:
      return `☕ ${window.i18next.t('pmwjs_cafe_daily_specials')}`;
    case EmailSingleRewrite.CHURCH_EVEMTS:
      return `📖 ${window.i18next.t('pmwjs_church_events')}`;
    case EmailSingleRewrite.SALE:
      return `🛍️ ${window.i18next.t('pmwjs_store_sale')}`;
    case EmailSingleRewrite.DAILY_SPECIALS:
      return `☕ ${window.i18next.t('pmwjs_daily_specials')}`;
    case EmailSingleRewrite.SUNDAY_SERVICE:
      return `⛪ ${window.i18next.t('pmwjs_sunday_service')}`;
  }
};

export const getPromptSuggestionsReplacementPrompt = (value: PromptSuggestionsValue | null): string => {
  if (!value) {
    return '';
  }

  switch (value) {
    case DefaultPromptSuggestions.CHURCH_NEWLETTER:
      return window.i18next.t('pmwjs_email_redesign_for_church_newsletter');
    case DefaultPromptSuggestions.RESTAURANT_DAILY_SPECIALS:
      return window.i18next.t('pmwjs_email_redesign_for_restaurant_daily_specials');
    case DefaultPromptSuggestions.SALE:
      return window.i18next.t('pmwjs_email_redesign_for_sale');
    case DefaultPromptSuggestions.SCHOOL_NEWSLETTER:
      return window.i18next.t('pmwjs_email_redesign_for_school_newsletter');
    case RestaurantPromptSuggestions.BAR_MENU:
      return window.i18next.t('pmwjs_redesign_this_for_my_bar_menu');
    case RestaurantPromptSuggestions.HAPPY_HOUR:
      return window.i18next.t('pmwjs_redesign_this_for_my_happy_hour');
    case RestaurantPromptSuggestions.COCKTAIL_HOUR:
      return window.i18next.t('pmwjs_redesign_this_for_my_cocktail_hour');
    case RestaurantPromptSuggestions.CAFE_DAILY_SPECIALS:
      return window.i18next.t('pmwjs_redesign_this_for_my_cafe_daily_specials');
    case EmailSingleRewrite.CHURCH_EVEMTS:
      return window.i18next.t('pmwjs_email_rewrite_for_church_events');
    case EmailSingleRewrite.SALE:
      return window.i18next.t('pmwjs_email_rewrite_for_store_sale');
    case EmailSingleRewrite.DAILY_SPECIALS:
      return window.i18next.t('pmwjs_email_rewrite_for_daily_specials');
    case EmailSingleRewrite.SUNDAY_SERVICE:
      return window.i18next.t('pmwjs_email_rewrite_for_sunday_service');
  }
};

export const getPromptOptionTitle = (option: PromptOption): string => {
  switch (option) {
    case PromptOption.MOOD:
      return window.i18next.t('pmwjs_mood');
    case PromptOption.EMOJI_USAGE:
      return window.i18next.t('pmwjs_add_emojis');
    case PromptOption.IMAGE_MODEL:
      return window.i18next.t('pmwjs_image_model');
  }
};

export const getPromptOptionEnum = (option: PromptOption): typeof EmojiUsage | typeof Mood | typeof ImageModel => {
  switch (option) {
    case PromptOption.EMOJI_USAGE:
      return EmojiUsage;
    case PromptOption.MOOD:
      return Mood;
    case PromptOption.IMAGE_MODEL:
      return ImageModel;
  }
};

export const getPromptSuggestionsEnum = (promptSuggestions: PromptSuggestions): PromptSuggestionsEnum => {
  switch (promptSuggestions) {
    case PromptSuggestions.RESTAURANT:
      return RestaurantPromptSuggestions;
    case PromptSuggestions.EMAIL_SINGLE_REWRITE:
      return EmailSingleRewrite;
    case PromptSuggestions.DEFAULT:
    default:
      return DefaultPromptSuggestions;
  }
};

export const getPromptOptionSelectorLayoutType = (option: PromptOption): PromptOptionSelectorLayoutType => {
  switch (option) {
    case PromptOption.EMOJI_USAGE:
    case PromptOption.MOOD:
      return PromptOptionSelectorLayoutType.PILLS;
    case PromptOption.IMAGE_MODEL:
      return PromptOptionSelectorLayoutType.CARDS;
  }
};

export const getEditableComponentCheckboxContent = (
  editableComponent: EditableComponent,
  editableComponentsMap: EditableComponentsMap,
  labelIconClassName: string,
  textClassName: string
): Filter => {
  switch (editableComponent) {
    case EditableComponent.IMAGE:
      return {
        label: window.i18next.t('pmwjs_images'),
        labelIcon: 'icon-image',
        dataValue: EditableComponent.IMAGE,
        isChecked: editableComponentsMap[EditableComponent.IMAGE] ?? false,
        checkboxType: CHECKBOX_TYPE.PRIMARY,
        checkboxSize: IconSize.SIZE_ICON_16,
        className: `spacing-m-b-0 spacing-p-t-2 spacing-p-b-2`,
        labelIconClassName: labelIconClassName,
        textSize: TextSize.XXSMALL,
        textClassName: textClassName,
      };
    case EditableComponent.TEXT:
      return {
        label: window.i18next.t('pmwjs_text'),
        labelIcon: 'icon-text',
        dataValue: EditableComponent.TEXT,
        isChecked: editableComponentsMap[EditableComponent.TEXT] ?? false,
        checkboxType: CHECKBOX_TYPE.PRIMARY,
        checkboxSize: IconSize.SIZE_ICON_16,
        className: `spacing-m-b-0 spacing-p-t-2 spacing-p-b-2`,
        labelIconClassName: labelIconClassName,
        textSize: TextSize.XXSMALL,
        textClassName: textClassName,
      };
  }
};

export const getPromptOptionsToUse = (editableComponentMap: EditableComponentsMap, promptOptionsForEditableComponents: PromptOptionsForEditableComponents): PromptOption[] => {
  const promptOptions: PromptOption[] = [];
  const keys = Object.keys(editableComponentMap) as EditableComponent[];

  if (keys.length === 1) {
    return promptOptionsForEditableComponents[keys[0]] ?? [];
  }

  keys.forEach((editableComponent) => {
    if (editableComponentMap[editableComponent]) {
      promptOptions.push(...(promptOptionsForEditableComponents[editableComponent] ?? []));
    }
  });

  return Array.from(new Set(promptOptions));
};

export class AiRedesignPromptBoxIssue extends Error {
  icon: string;
  type: RedesignIssueType;

  constructor(message: string, icon: string, type: RedesignIssueType) {
    super(message);
    this.icon = icon;
    this.type = type;
  }
}
